// SPDX-License-Identifier: AGPL-3.0-or-later

import * as ChannelCommands from '@app/features/channel/commands/ChannelCommands';
import {showChannelErrorModal} from '@app/features/channel/components/alerts/ChannelErrorModalUtils';
import {getAcceptStringFiltered, getAssetFormatErrorMessage} from '@app/features/expressions/utils/AssetFormatCopy';
import {readImageFileAsUploadDataUrl} from '@app/features/expressions/utils/ImageUploadFileUtils';
import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import {IMAGE_MAX_SIZE_BYTES} from '@app/features/app/config/I18nDisplayConstants';
import {openFilePicker} from '@app/features/messaging/utils/FilePickerUtils';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {ImageIcon, TrashIcon, UploadSimpleIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useState} from 'react';

const CHAT_BACKGROUND_DESCRIPTOR = msg({
	message: 'Chat background',
	comment: 'Short label for the chat background settings section. Keep it concise.',
});
const UPLOAD_AN_IMAGE_SHOWN_BEHIND_MESSAGES_IN_THIS_DESCRIPTOR = msg({
	message: 'Upload an image shown behind messages in this conversation.',
	comment: 'Description for the chat background settings section.',
});
const CHANGE_BACKGROUND_DESCRIPTOR = msg({
	message: 'Change background',
	comment: 'Button label for picking a new chat background image. Keep it concise.',
});
const UPLOAD_BACKGROUND_DESCRIPTOR = msg({
	message: 'Upload background',
	comment: 'Button label for uploading a chat background image. Keep it concise.',
});
const REMOVE_BACKGROUND_DESCRIPTOR = msg({
	message: 'Remove background',
	comment: 'Button label for removing the chat background image. Keep it concise.',
});
const BACKGROUND_UPDATED_DESCRIPTOR = msg({
	message: 'Chat background updated.',
	comment: 'Toast shown after a chat background is saved.',
});
const BACKGROUND_REMOVED_DESCRIPTOR = msg({
	message: 'Chat background removed.',
	comment: 'Toast shown after a chat background is removed.',
});
const BACKGROUND_FILE_IS_TOO_LARGE_DESCRIPTOR = msg({
	message: 'Background file is too large. Choose a file smaller than {imageMaxSizeLabel}.',
	comment: 'Error message when the chosen chat background exceeds the size limit. Preserve {imageMaxSizeLabel}.',
});
const BACKGROUND_FILE_IS_TOO_LARGE_TITLE_DESCRIPTOR = msg({
	message: 'Background file is too large',
	comment: 'Title of the error modal shown when the chosen chat background exceeds the size limit.',
});
const UNSUPPORTED_BACKGROUND_FORMAT_DESCRIPTOR = msg({
	message: 'Unsupported background format',
	comment: 'Title of the error modal shown when the chosen chat background format is unsupported.',
});
const INVALID_BACKGROUND_IMAGE_DESCRIPTOR = msg({
	message: 'Invalid image',
	comment: 'Title of the error modal shown when the chosen chat background cannot be used.',
});
const INVALID_BACKGROUND_IMAGE_TRY_ANOTHER_DESCRIPTOR = msg({
	message: 'That file could not be used as a background. Try another image.',
	comment: 'Body of the error modal shown when the chosen chat background cannot be used.',
});

interface ChatBackgroundSectionProps {
	scope: 'channel' | 'guild';
	id: string;
	backgroundHash: string | null;
}

export const ChatBackgroundSection: React.FC<ChatBackgroundSectionProps> = observer(({scope, id, backgroundHash}) => {
	const {i18n} = useLingui();
	const [isSaving, setIsSaving] = useState(false);
	const previewUrl =
		scope === 'channel'
			? AvatarUtils.getChannelBackgroundURL({id, backgroundHash})
			: AvatarUtils.getGuildBackgroundURL({id, backgroundHash});
	const saveBackground = useCallback(
		async (background: string | null) => {
			setIsSaving(true);
			try {
				if (scope === 'channel') {
					await ChannelCommands.update(id, {background});
				} else {
					await GuildCommands.update(id, {background});
				}
				ToastCommands.createToast({
					type: 'success',
					children:
						background === null
							? i18n._(BACKGROUND_REMOVED_DESCRIPTOR)
							: i18n._(BACKGROUND_UPDATED_DESCRIPTOR),
				});
			} catch {
				showChannelErrorModal({
					title: i18n._(INVALID_BACKGROUND_IMAGE_DESCRIPTOR),
					message: i18n._(INVALID_BACKGROUND_IMAGE_TRY_ANOTHER_DESCRIPTOR),
					dataFlx: 'channel.chat-background-section.invalid-image.generic-error-modal',
				});
			} finally {
				setIsSaving(false);
			}
		},
		[scope, id, i18n],
	);
	const handlePickFile = useCallback(async () => {
		const [file] = await openFilePicker({accept: getAcceptStringFiltered('banner', false)});
		if (!file) {
			return;
		}
		if (file.size > IMAGE_MAX_SIZE_BYTES) {
			showChannelErrorModal({
				title: i18n._(BACKGROUND_FILE_IS_TOO_LARGE_TITLE_DESCRIPTOR),
				message: i18n._(BACKGROUND_FILE_IS_TOO_LARGE_DESCRIPTOR, {
					imageMaxSizeLabel: formatFileSize(i18n.locale, IMAGE_MAX_SIZE_BYTES),
				}),
				dataFlx: 'channel.chat-background-section.file-too-large.generic-error-modal',
			});
			return;
		}
		try {
			const base64 = await readImageFileAsUploadDataUrl(file);
			await saveBackground(base64);
		} catch {
			showChannelErrorModal({
				title: i18n._(UNSUPPORTED_BACKGROUND_FORMAT_DESCRIPTOR),
				message: getAssetFormatErrorMessage(i18n, 'banner', 'unsupported_mime'),
				dataFlx: 'channel.chat-background-section.unsupported-format.generic-error-modal',
			});
		}
	}, [i18n, saveBackground]);
	const handleRemove = useCallback(() => {
		void saveBackground(null);
	}, [saveBackground]);
	return (
		<div data-flx="channel.chat-background-section.container">
			<div data-flx="channel.chat-background-section.label">{i18n._(CHAT_BACKGROUND_DESCRIPTOR)}</div>
			<div data-flx="channel.chat-background-section.description">
				{i18n._(UPLOAD_AN_IMAGE_SHOWN_BEHIND_MESSAGES_IN_THIS_DESCRIPTOR)}
			</div>
			{previewUrl ? (
				<div data-flx="channel.chat-background-section.preview">
					<img src={previewUrl} alt="" data-flx="channel.chat-background-section.preview-image" />
				</div>
			) : (
				<div data-flx="channel.chat-background-section.empty">
					<ImageIcon size={32} data-flx="channel.chat-background-section.empty-icon" />
				</div>
			)}
			<div data-flx="channel.chat-background-section.actions">
				<Button
					variant="secondary"
					onClick={() => void handlePickFile()}
					disabled={isSaving}
					leftIcon={<UploadSimpleIcon size={16} />}
					data-flx="channel.chat-background-section.button.pick"
				>
					{backgroundHash ? i18n._(CHANGE_BACKGROUND_DESCRIPTOR) : i18n._(UPLOAD_BACKGROUND_DESCRIPTOR)}
				</Button>
				{backgroundHash && (
					<Button
						variant="secondary"
						onClick={handleRemove}
						disabled={isSaving}
						leftIcon={<TrashIcon size={16} />}
						data-flx="channel.chat-background-section.button.remove"
					>
						{i18n._(REMOVE_BACKGROUND_DESCRIPTOR)}
					</Button>
				)}
			</div>
		</div>
	);
});
